import { type Connection, listen } from 'coframe'

import * as types from '@script/types'

import { BlendEventType } from '../api/events/types'
import { useStore } from '../stores/useStore'
import { getBlendEventMessage } from './utils'

/** Generic event bus, lives on frame side of truework-js channel */
const internal = listen<types.InternalEvents>('truework-js')

/** Blend-specific event bus, lives on frame side of truework-js channel */
export const blendEvents = listen<{
  event: types.BlendEventPayload
  complete: types.BlendOnCompletePayload
}>('truework-js')

/** Emit an event on the Blend event bus */
export function emitBlendEvent({
  eventType,
  orderId,
  eventMessage,
}: {
  eventType: BlendEventType
  orderId?: string
  eventMessage?: string
}) {
  blendEvents.emit('event', {
    eventType,
    eventDetails: {
      orderID: orderId,
      eventMessage: eventMessage ?? getBlendEventMessage(eventType),
    },
  })
}

/** Wrap the coframe connection in order to forward certain generic events to the Blend event bus */
export const events: Connection<types.InternalEvents> = (() => {
  return {
    on: internal.on.bind(internal),
    emit: async (type, payload) => {
      const result = await internal.emit(type, payload)

      /** Map outbound events (frame => parent) to Blend event bus */
      if (type === 'complete' || type === 'sessionRefreshNeeded') {
        // Blend uses this as a trigger to pull latest data from Truework
        blendEvents.emit('complete', { orderID: useStore.getState().session?.blend_id })
      }

      return result
    },
  }
})()
export type Events = typeof events
